import React, { useMemo, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import qmsTeamActions from "../../../_actions/_qmsActions/qmsTeamActions";
import qmsWorkflowActions from "../../../_actions/_qmsActions/qmsWorkflowActions";

export default function CmtIssueStagePrepopulate(props) {
  const {
    divisionId,
    tabId,
    starter,
    setStarter,
    selectedWorkflow,
    setWorkflow,
    initialStageTitle,
    setInitialStageTitle,
    selectedPresetStage,
    setPresetStage,
    selectedAssignTo,
    setAssignTo,
  } = props;

  const dispatch = useDispatch();
  const qmsTeams = useSelector((state) =>
    divisionId ? state.qms.qmsTeams[divisionId] || null : null
  );
  const workflows = useSelector((state) => state.qms.openWorkflowLists[tabId] || null);
  const cmtStages = useSelector((state) => state.qms.cmtStages[divisionId] || null);
  const workflowOptions = useMemo(
    () => (workflows || []).map((w) => ({ value: w.workflowId, label: w.name })),
    [workflows]
  );

  const qmsTeamOptions = useMemo(
    () => (qmsTeams || []).map((qt) => ({ label: qt.name, value: qt.qmsTeamId })),
    [qmsTeams]
  );

  const cmtStageOptions = useMemo(
    () => [
      { value: 0, label: "Custom (Manual Entry)" },
      ...(cmtStages || []).map((cs) => ({
        value: cs.cmtStageId,
        label: cs.title,
      })),
    ],
    [cmtStages]
  );

  useEffect(() => {
    dispatch(qmsTeamActions.getAll(divisionId));
    dispatch(qmsWorkflowActions.getAll(divisionId, tabId));
  }, []);

  // When preset stage is selected, prepopulate stage title and assign to
  useEffect(() => {
    if (selectedPresetStage) {
      const cs = (cmtStages || []).find(
        (s) => s.cmtStageId === selectedPresetStage.value
      );
      setInitialStageTitle(cs ? cs.title || "" : initialStageTitle || "");
      if (qmsTeamOptions && cs) {
        if (cs.defaultOwnerTeamId) {
          const qto = qmsTeamOptions.find((o) => o.value === cs.defaultOwnerTeamId);
          setAssignTo(qto || null);
        } else setAssignTo(null);
      }
    } else setPresetStage(cmtStageOptions[0]);
  }, [selectedPresetStage]);

  // If initial stage title or assign to values deviate from selected preset stage's values,
  // clear preset stage
  useEffect(() => {
    if (selectedPresetStage) {
      const cs = (cmtStages || []).find(
        (s) => s.cmtStageId === selectedPresetStage.value
      );
      if (cs) {
        if (initialStageTitle !== cs.title) setPresetStage(cmtStageOptions[0]);
        if (
          (!selectedAssignTo && cs.defaultOwnerTeamId) ||
          (selectedAssignTo && selectedAssignTo.value !== (cs.defaultOwnerTeamId || null))
        ) {
          setPresetStage(cmtStageOptions[0]);
        }
      }
    }
  }, [initialStageTitle, selectedAssignTo]);

  return (
    <div className="card bg-lightgray">
      <div className="card-body p-3">
        <h5 className="text-muted">
          Prepopulate Stage(s) <small>(Optional)</small>
        </h5>
        <div className="row mb-2">
          <div className="form-group col-12 mb-0">
            <div className="custom-control custom-radio">
              <input
                type="radio"
                name="none"
                id="none"
                checked={starter === "none"}
                onChange={() => setStarter("none")}
                className="custom-control-input"
              />
              <label htmlFor="none" className="custom-control-label">
                <b>None</b>
              </label>
            </div>
          </div>
        </div>
        <div className="row mb-1">
          <div className="form-group col-12 mb-0">
            <div className="custom-control custom-radio">
              <input
                type="radio"
                name="workflow"
                id="workflow"
                checked={starter === "workflow"}
                onChange={() => setStarter("workflow")}
                className="custom-control-input"
              />
              <label htmlFor="workflow" className="custom-control-label">
                <b>Workflow</b>
                <FontAwesomeIcon icon="code-branch" className="fa-fw mx-1" />
                (Multiple Stages)
              </label>
            </div>
          </div>
        </div>
        <div className="d-flex flex-row mb-2">
          <div style={{ width: "5%" }}></div>
          <div className="flex-grow-1">
            <div className="card mb-2">
              <div className="card-body p-2">
                <div className="row mb-0">
                  <div className="form-group col-12 mb-0">
                    <label htmlFor="workflow" className="mb-0">
                      Select Workflow:
                    </label>
                    <Select
                      name="workflow"
                      id="workflow"
                      value={selectedWorkflow}
                      options={workflowOptions}
                      onChange={(selected) => setWorkflow(selected)}
                      isClearable
                      isDisabled={starter !== "workflow"}
                      styles={{
                        // Fixes the overlapping problem of the component
                        menu: (provided) => ({ ...provided, zIndex: 9999 }),
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="row mb-1">
          <div className="form-group col-12 mb-0">
            <div className="custom-control custom-radio">
              <input
                type="radio"
                name="stage"
                id="stage"
                checked={starter === "stage"}
                onChange={() => setStarter("stage")}
                className="custom-control-input"
              />
              <label htmlFor="stage" className="custom-control-label">
                <b>Starter Stage</b> (Single Stage)
              </label>
            </div>
          </div>
        </div>
        <div className="d-flex flex-row mb-0">
          <div style={{ width: "5%" }}></div>
          <div className="flex-grow-1">
            <div className="card w-100">
              <div className="card-body p-2">
                <div className="row mb-2">
                  <div className="form-group col-7 mb-0">
                    <label htmlFor="presetStage" className="mb-0">
                      Preset Stage:
                    </label>
                    <Select
                      name="presetStage"
                      id="presetStage"
                      value={selectedPresetStage}
                      options={cmtStageOptions}
                      onChange={(selected) => setPresetStage(selected)}
                      isClearable
                      isDisabled={starter !== "stage"}
                      styles={{
                        // Fixes the overlapping problem of the component
                        menu: (provided) => ({ ...provided, zIndex: 9999 }),
                      }}
                    />
                  </div>
                </div>
                <div className="row mb-0">
                  <div className="form-group col-md-6 mb-0 pr-1">
                    <label htmlFor="initialStageTitle" className="mb-0">
                      <b>Initial Stage Title:</b>*
                      <small> (Req'd for starter stage)</small>
                    </label>
                    <input
                      id="initialStageTitle"
                      name="initialStageTitle"
                      className="form-control"
                      maxLength="30"
                      value={initialStageTitle || ""}
                      onChange={(e) => setInitialStageTitle(e.target.value)}
                      disabled={starter !== "stage"}
                    />
                  </div>
                  <div className="form-group col-md-6 mb-0 pl-1">
                    <label htmlFor="assignTo" className="mb-0">
                      Assign To:
                    </label>
                    <Select
                      name="assignTo"
                      id="assignTo"
                      value={selectedAssignTo}
                      options={qmsTeamOptions}
                      onChange={(selected) => setAssignTo(selected)}
                      isClearable
                      isDisabled={starter !== "stage"}
                      styles={{
                        // Fixes the overlapping problem of the component
                        menu: (provided) => ({ ...provided, zIndex: 9999 }),
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
